import { Layout, Table, Button, Image, Modal, Skeleton, List, Tag, Typography } from 'antd';
import {
  useGetSurveysQuery,
  useDeleteSurveyIdMutation,
  useGetAssetsFromSurveyQuery,
} from '../features/api/apiSlice';
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { ExclamationCircleFilled } from '@ant-design/icons';
import useIsMobile from '../utils/mediaQuery';
import * as maptilersdk from '@maptiler/sdk';
import SurveyPreviewModal from './SurveyPreviewModal';
import { getPhotoDisplayUrl } from '../utils/photoUtilities';
import LazyPhoto from './assets/LazyPhoto';

const { confirm } = Modal;
const { Text, Paragraph } = Typography;

const MUTED = '#999';
const NA = <span style={{ color: MUTED }}>N/A</span>;

function buildStaticMapUrl(survey, size = '120x90') {
  const parseRouteCoords = (route_geojson) => {
    if (!route_geojson) return null;
    try {
      const geo = typeof route_geojson === 'string' ? JSON.parse(route_geojson) : route_geojson;
      return geo?.coordinates ?? null;
    } catch {
      return null;
    }
  };

  const coords = parseRouteCoords(survey.route_geojson);
  const members = Array.isArray(survey.members) ? survey.members : [];
  const edges = Array.isArray(survey.edges) ? survey.edges : [];

  let edgeCoords = [];
  if (edges.length > 0 && members.length > 0) {
    const locMap = new Map();
    members.forEach((m) => {
      if (m.longitude !== null && m.latitude !== null)
        locMap.set(m.asset_id, [m.longitude, m.latitude]);
    });
    edges.forEach((edge) => {
      const a = locMap.get(edge.asset_a_id);
      const b = locMap.get(edge.asset_b_id);
      if (a && b) edgeCoords.push([a, b]);
    });
  }

  let allCoords = [];
  if (edgeCoords.length > 0) edgeCoords.forEach(([a, b]) => allCoords.push(a, b));
  if (allCoords.length === 0 && coords?.length) allCoords = [...coords];
  if (members.length) members.forEach((m) => allCoords.push([m.longitude, m.latitude]));
  if (allCoords.length === 0) allCoords.push([survey.longitude, survey.latitude]);

  const lngs = allCoords.map((c) => c[0]);
  const lats = allCoords.map((c) => c[1]);
  const buf = 0.15;
  const lngR = Math.max(...lngs) - Math.min(...lngs);
  const latR = Math.max(...lats) - Math.min(...lats);
  const bbox = `${Math.min(...lngs) - lngR * buf},${Math.min(...lats) - latR * buf},${Math.max(...lngs) + lngR * buf},${Math.max(...lats) + latR * buf}`;

  const paths = [];
  if (edgeCoords.length > 0) {
    edgeCoords.forEach(([a, b]) => {
      paths.push(`stroke:blue|width:3|fill:none|${a[0]},${a[1]}|${b[0]},${b[1]}`);
    });
  } else if (coords?.length) {
    paths.push(`stroke:blue|width:3|fill:none|${coords.map((c) => `${c[0]},${c[1]}`).join('|')}`);
  }

  const markersPairs = members.length
    ? members.map((m) => `${m.longitude},${m.latitude}`).join('|')
    : `${survey.longitude},${survey.latitude}`;

  const mapId = maptilersdk?.config?.mapId || 'nl-cartiqo-topo';
  const key = maptilersdk?.config?.apiKey || process.env.REACT_APP_MAPTILER_KEY;

  let url = `https://api.maptiler.com/maps/${mapId}/static/${bbox}/${size}.png?key=${encodeURIComponent(key)}`;
  paths.forEach((p) => {
    url += `&path=${encodeURIComponent(p)}`;
  });
  url += `&markers=${encodeURIComponent(markersPairs)}`;
  return url;
}

export default function Surveys(props) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { setSurveysSelected } = props;

  const [photosMap, setPhotosMap] = useState(new Map());
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const [deleteSurveyId] = useDeleteSurveyIdMutation();

  const { data: surveysFromApi, isLoading: surveysLoading } = useGetSurveysQuery({
    projectId,
    includeMembers: true,
    includeEdges: true,
  });

  const { data: assetsFromApi, isLoading: assetsLoading } = useGetAssetsFromSurveyQuery(
    {
      projectId,
      surveyIds: surveysFromApi?.map((survey) => survey.id),
      fetchPhotos: true,
      fetchAssets: false,
    },
    {
      skip: !surveysFromApi || surveysFromApi.length === 0,
    }
  );

  useEffect(() => {
    if (assetsFromApi) {
      const newMap = new Map();
      assetsFromApi.surveys.forEach(({ surveyId, assets }) => {
        const allPhotos = assets.flatMap((asset) => asset.photos || []);
        newMap.set(surveyId, allPhotos);
      });
      setPhotosMap(newMap);
    }
  }, [assetsFromApi]);

  const getFirstPhotoUrl = (surveyId) => {
    const firstPhoto = photosMap.get(surveyId)?.[0];
    if (!firstPhoto) return null;
    const url = getPhotoDisplayUrl(firstPhoto);
    return url === '/placeholder.svg' ? null : url;
  };

  const isMobile = useIsMobile();

  const showDeleteConfirm = (surveyId) => {
    confirm({
      title: 'Are you sure you want to delete this survey?',
      icon: <ExclamationCircleFilled />,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteSurveyId({ surveyId, projectId }).unwrap();
        } catch (e) {
          console.error(e);
        }
      },
    });
  };

  const navigateToSurveyOnMap = (survey) => {
    navigate(`/projects/${projectId}/map?surveyId=${survey.id}`);
  };

  const openPreview = (survey) => setPreviewSurvey(survey);
  const closePreview = () => setPreviewSurvey(null);

  const columns = useMemo(
    () => [
      {
        title: 'Photo',
        key: 'photo',
        align: 'center',
        width: 120,
        render: (_, survey) => {
          const firstPhoto = photosMap.get(survey.id)?.[0];
          const src = getFirstPhotoUrl(survey.id);
          if (!src || !firstPhoto)
            return (
              <div
                style={{
                  width: 90,
                  height: 65,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5',
                  borderRadius: 4,
                  color: MUTED,
                  fontSize: 12,
                }}
              >
                No Photo
              </div>
            );
          return (
            <div style={{ width: 90, height: 65, borderRadius: 4, overflow: 'hidden' }}>
              <LazyPhoto photo={firstPhoto} alt="Survey photo" objectFit="cover" />
            </div>
          );
        },
      },
      {
        title: 'Location',
        key: 'location',
        align: 'center',
        render: (_, survey) => (
          <Image
            style={{ cursor: 'pointer', borderRadius: 4 }}
            onClick={() => navigateToSurveyOnMap(survey)}
            preview={false}
            src={buildStaticMapUrl(survey, '100x100')}
            alt={`Map for survey ${survey.id}`}
            fallback="/placeholder.svg"
          />
        ),
      },
      {
        title: 'Note',
        dataIndex: 'note',
        align: 'center',
        ellipsis: true,
        render: (note) =>
          note ? (
            <Paragraph
              style={{ margin: 0, maxWidth: 240, textAlign: 'left', display: 'inline-block' }}
              ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
            >
              {note}
            </Paragraph>
          ) : (
            NA
          ),
      },
      {
        title: 'Assets',
        key: 'assets',
        align: 'center',
        width: 80,
        render: (_, survey) => {
          const count = survey.member_count ?? survey.members?.length ?? 0;
          return <Tag color="blue">{count}</Tag>;
        },
      },
      {
        title: 'Created By',
        key: 'created_by',
        align: 'center',
        ellipsis: true,
        render: (_, record) => record.email || <span style={{ color: MUTED }}>Unknown</span>,
      },
      {
        title: 'Date',
        key: 'date',
        align: 'center',
        sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
        defaultSortOrder: 'descend',
        render: (_, survey) => dayjs(survey.created_at).format('DD MMM YYYY'),
        filters: surveysLoading
          ? []
          : [
              ...new Set(
                (surveysFromApi || []).map(({ created_at }) =>
                  dayjs(created_at).format('DD MMM YYYY')
                )
              ),
            ].map((val) => ({ text: val, value: val })),
        onFilter: (value, record) => value === dayjs(record.created_at).format('DD MMM YYYY'),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        render: (_, record) => (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Button size="small" onClick={() => openPreview(record)}>
              Preview
            </Button>
            <Button size="small" danger onClick={() => showDeleteConfirm(record.id)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surveysFromApi, surveysLoading, photosMap]
  );

  return (
    <Layout>
      {!isMobile && (
        <Table
          style={{ width: '100%', tableLayout: 'fixed' }}
          loading={surveysLoading}
          rowSelection={{
            type: 'checkbox',
            onChange: (keys) => {
              setSurveysSelected(keys.map((k) => surveysFromApi.find((s) => s.id == k)));
            },
          }}
          columns={columns}
          dataSource={surveysFromApi}
        />
      )}

      {isMobile && (
        <List
          itemLayout="vertical"
          style={{ margin: '75px 20px' }}
          size="large"
          loading={surveysLoading}
          pagination={{ position: 'bottom', align: 'center' }}
          dataSource={surveysFromApi || []}
          renderItem={(item) => {
            const photoSrc = getFirstPhotoUrl(item.id);
            return (
              <List.Item
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                key={item.id}
                actions={[
                  <a key="preview" onClick={() => openPreview(item)}>
                    Preview
                  </a>,
                  <a key="map" onClick={() => navigateToSurveyOnMap(item)}>
                    View on Map
                  </a>,
                  <a key="delete" onClick={() => showDeleteConfirm(item.id)}>
                    Delete
                  </a>,
                ]}
                extra={
                  photoSrc ? (
                    <Image
                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 6 }}
                      src={photoSrc}
                      preview={{ src: photoSrc }}
                    />
                  ) : null
                }
              >
                <Skeleton style={{ width: '100%' }} loading={surveysLoading} active>
                  <List.Item.Meta
                    style={{ width: '100%' }}
                    title={item.note || `Survey #${item.id}`}
                    description={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>{item.email}</span>
                        <small>{dayjs(item.created_at).format('DD MMM YYYY')}</small>
                      </div>
                    }
                  />
                </Skeleton>
              </List.Item>
            );
          }}
        />
      )}

      <SurveyPreviewModal
        previewSurvey={previewSurvey}
        assetsLoading={assetsLoading}
        onClose={closePreview}
      />
    </Layout>
  );
}
